import React, { useEffect, useState } from "react";
import { BellRing, CheckCircle2 } from "lucide-react";

import { useParams, useLocation, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, ChevronRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import NotFound from "@/pages/NotFound";
import Seo, {
  serviceSchema,
  faqSchema,
  breadcrumbSchema,
  localBusinessSchema,
} from "@/components/Seo";
import { getServicePage, SERVICE_PAGES, PRICING_KEYS, priceInfoFor, PRIMARY_PRICING_KEY } from "@/lib/servicesContent";
import { promoEligible, promoPrice, promoPctFor, fmtMoney } from "@/lib/promo";
import { CONTACT, COMING_SOON_SLUGS, HIDDEN_SERVICE_SLUGS } from "@/lib/content";
import { api, API } from "@/lib/api";
import Calculator from "@/components/Calculator";
import { toast } from "sonner";
import PpfPackages from "@/components/PpfPackages";
import TintVisualizer from "@/components/TintVisualizer";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function ServicePage() {
  const { slug } = useParams();
  const location = useLocation();
  const svc = getServicePage(slug);
  const [priceInfo, setPriceInfo] = useState({ from: null, promoFrom: null });
  const [selPkg, setSelPkg] = useState("");
  // Owner-managed before/after photos uploaded from CRM → Website Photos
  const [dynPairs, setDynPairs] = useState([]);
  // Embedded booking flow (full Calculator 2.0) — service CTAs pre-select the
  // matching pricing service and scroll to the configurator on this page.
  const [preset, setPreset] = useState({ key: null, n: 0 });

  useEffect(() => {
    if (!svc) return;
    api.get("/pricing/config").then(({ data }) => {
      setPriceInfo(priceInfoFor(svc.serviceSlug, data.services || [], data.promo));
    }).catch(() => {});
  }, [svc]);

  useEffect(() => {
    if (!svc) return;
    setDynPairs([]);
    api.get("/crm/site-media/public", { params: { section: slug } }).then(({ data }) => {
      const url = (id) => `${API}/crm/site-media/${id}/file`;
      const befores = data.filter((m) => m.role === "before");
      const afters = data.filter((m) => m.role === "after");
      const pairs = [];
      for (let i = 0; i < Math.min(befores.length, afters.length); i++) {
        pairs.push({ before: url(befores[i].id), after: url(afters[i].id) });
      }
      setDynPairs(pairs);
    }).catch(() => {});
  }, [svc, slug]);

  // Canonical service URLs are top-level (/ceramic-coating-edmonton).
  // Tolerate legacy short slugs and the old PPF slug.
  if (!svc) {
    if (slug === "paint-protection-film-edmonton") return <Navigate to="/ppf-edmonton" replace />;
    // Decon is an add-on to Exterior Detailing now (owner, 2026-11)
    if (slug === "decontamination-wash-edmonton" || slug === "decontamination-wash")
      return <Navigate to="/exterior-detailing-edmonton" replace />;
    const full = `${slug}-edmonton`;
    if (SERVICE_PAGES[full]) return <Navigate to={`/${full}`} replace />;
    return <Navigate to="/" replace />;
  }
  // /services/<slug> permanently moved to /<slug>
  if (location.pathname.startsWith("/services/")) {
    return <Navigate to={`/${slug}`} replace />;
  }

  // Hidden services (e.g. window tint — off the site for now) behave as 404.
  if (HIDDEN_SERVICE_SLUGS.includes(svc.serviceSlug)) {
    return <NotFound />;
  }

  const soon = COMING_SOON_SLUGS.includes(svc.serviceSlug);

  // CTA → pre-select this page's service in the embedded configurator and
  // scroll to it (no redirect away from the SEO page).
  const startBooking = (key) => {
    setPreset((p) => ({ key: key || PRIMARY_PRICING_KEY[svc.serviceSlug] || null, n: p.n + 1 }));
    document.getElementById("calculator")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const relatedSlugs = svc.related || Object.keys(SERVICE_PAGES).filter((s) => s !== slug).slice(0, 3);
  // Static content photos + owner-uploaded photos from CRM → Website Photos
  const allPairs = [...(svc.beforeAfter || []), ...dynPairs];

  return (
    <div className="min-h-screen bg-[#0c0c0f] text-white" data-testid={`service-page-${slug}`}>
      <Seo
        title={svc.seoTitle}
        description={svc.seoDescription}
        keywords={svc.keywords}
        path={`/${slug}`}
        image={svc.hero}
        jsonLd={[
          localBusinessSchema({ contact: CONTACT }),
          serviceSchema({
            name: svc.title,
            description: svc.seoDescription,
            slug,
          }),
          faqSchema(svc.faq),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
            { name: svc.title, path: `/${slug}` },
          ]),
        ]}
      />

      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center pt-32 pb-16 overflow-hidden isolate">
        <div className="absolute inset-0 z-0">
          <img
            src={svc.hero}
            alt={`${svc.title} in Edmonton`}
            className="w-full h-full object-cover object-center opacity-70 saturate-[0.9]"
            loading="eager"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full">
          <nav
            aria-label="Breadcrumb"
            className="flex items-center gap-2 text-xs font-mono-alt text-white/50 mb-8"
          >
            <Link to="/" className="hover:text-white transition-colors">
              Home
            </Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/services" className="hover:text-white transition-colors">
              Services
            </Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white/80">{svc.title}</span>
          </nav>

          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-[#1F5EFF]" />
            <span className="font-mono-alt text-[12px] text-[#1F5EFF]">{svc.tag}</span>
            {soon && (
              <span
                data-testid="service-soon-badge"
                className="px-3 py-1 rounded-full border border-amber-400/30 bg-amber-400/10 text-amber-300 font-mono-alt text-[10px] uppercase tracking-widest"
              >
                {svc.soonBadge || "Coming Soon"}
              </span>
            )}
            {priceInfo.from != null && !soon && (
              <>
                <span className="text-white/20">·</span>
                {priceInfo.promoFrom != null ? (
                  <span className="font-mono-alt text-[11px]" data-testid="service-hero-promo-price">
                    <span className="text-white/35 line-through mr-1.5">From {fmtMoney(priceInfo.from)}</span>
                    <span className="text-emerald-300">From {fmtMoney(priceInfo.promoFrom)} CAD</span>
                  </span>
                ) : (
                  <span className="font-mono-alt text-[11px] text-white/70">
                    From {fmtMoney(priceInfo.from)} CAD
                  </span>
                )}
              </>
            )}
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
            className="font-display text-4xl md:text-6xl lg:text-7xl font-light tracking-tighter leading-[1.02] max-w-4xl"
          >
            {svc.h1}
            <span className="text-[#1F5EFF] blue-text-glow">.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="mt-8 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed"
          >
            {svc.intro}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="mt-10"
          >
            {soon ? (
              <div className="max-w-xl">
                <div className="font-mono-alt text-[11px] text-amber-300 tracking-widest uppercase mb-4">
                  {svc.availabilityNote || "Launching soon at our Edmonton studio"}
                </div>
                <WaitlistForm service={svc.title} testid="service-hero-waitlist" pkg={selPkg} />
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  data-testid="service-hero-cta"
                  onClick={() => startBooking()}
                  className="group inline-flex items-center gap-3 px-7 py-4 rounded-full bg-[#1F5EFF] text-white font-semibold hover:bg-white hover:text-black transition-colors"
                >
                  Get Exact Price &amp; Book
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>
                <a
                  href={`tel:${CONTACT.phone.replace(/[^\d+]/g, "")}`}
                  className="inline-flex items-center gap-3 px-7 py-4 rounded-full border border-white/25 text-white hover:border-white hover:bg-white/5 transition-colors"
                >
                  Call {CONTACT.phone}
                </a>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Interactive coverage/shade visualizers */}
      {svc.serviceSlug === "ppf" && (
        <PpfPackages />
      )}
      {soon && svc.serviceSlug === "window-tint" && (
        <TintVisualizer onSelect={setSelPkg} />
      )}

      {/* Content sections */}
      <section className="relative py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-10 space-y-16">
          {svc.sections.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7 }}
            >
              <div className="font-mono-alt text-[11px] text-[#1F5EFF] mb-3">
                {String(i + 1).padStart(2, "0")}
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight text-white leading-tight mb-6">
                {s.heading}
              </h2>
              <p className="text-white/70 text-base md:text-lg leading-relaxed">
                {s.body}
              </p>
              {s.heading === "Optional upgrade — Paint Decontamination" && (
                <button
                  type="button"
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent("wtd-addon-preselect",
                      { detail: { service: "exterior-detail", addon: "paint_decon" } }));
                    document.getElementById("calculator")?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  data-testid="service-decon-cta"
                  className="mt-6 inline-flex items-center gap-3 px-6 py-3.5 rounded-full bg-[#1F5EFF] text-white font-semibold hover:bg-white hover:text-black transition-colors"
                >
                  Add Paint Decontamination — From $71
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* Before & after — real work photos (static content + CRM uploads) */}
      {allPairs.length > 0 && (
        <section className="relative pb-20 md:pb-28" data-testid="before-after-section">
          <div className="max-w-6xl mx-auto px-6 md:px-10">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-[#1F5EFF]" />
              <span className="font-mono-alt text-[12px] text-[#1F5EFF]">
                Real Results
              </span>
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight text-white mb-12">
              Before &amp; after — actual work from our studio.
            </h2>
            <div className="space-y-6 md:space-y-8">
              {allPairs.map((pair, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4" data-testid={`before-after-${i}`}>
                  {[["Before", pair.before], ["After", pair.after]].map(([label, src]) => (
                    <div key={label} className="relative rounded-xl overflow-hidden border border-white/12">
                      <img
                        src={src}
                        alt={`${svc.title} — ${label.toLowerCase()}`}
                        className="w-full aspect-[4/3] object-cover"
                        loading="lazy"
                      />
                      <span className={`absolute top-3 left-3 px-3 py-1 rounded-full text-[11px] font-mono-alt uppercase tracking-widest backdrop-blur-sm ${
                        label === "Before" ? "bg-black/60 text-white/70" : "bg-[#1F5EFF]/85 text-white"
                      }`}>
                        {label}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            {svc.beforeAfterNote && (
              <p className="text-white/40 text-sm mt-6">{svc.beforeAfterNote}</p>
            )}
          </div>
        </section>
      )}

      {/* Benefits grid */}
      <section className="relative py-20 md:py-28 bg-gradient-to-b from-transparent via-[#121216] to-transparent">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-[#1F5EFF]" />
            <span className="font-mono-alt text-[12px] text-[#1F5EFF]">
              What You Get
            </span>
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight text-white mb-12">
            Included with every {svc.title.toLowerCase()} install.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            {svc.benefits.map((b, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="flex items-start gap-4 p-5 rounded-xl bg-[#17171c] border border-white/12"
              >
                <div className="w-8 h-8 rounded-lg bg-[#1F5EFF]/15 border border-[#1F5EFF]/40 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-[#1F5EFF]" strokeWidth={2.5} />
                </div>
                <div className="text-white/85 pt-0.5">{b}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages & live pricing from the studio catalog */}
      {!soon && <CatalogPricing serviceSlug={svc.serviceSlug} onBook={startBooking} />}

      {/* Full booking flow (Calculator 2.0) — the same component as the hidden
          /calculator route, so D1–D5 vehicle pricing, packages, upsells and
          Grand Opening logic stay in one shared place. */}
      {!soon && <Calculator presetServiceKey={preset.key} presetNonce={preset.n} ppfFirst={slug === "ppf-edmonton"} />}

      {/* FAQ */}
      <section className="relative py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-[#1F5EFF]" />
            <span className="font-mono-alt text-[12px] text-[#1F5EFF]">FAQ</span>
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight text-white mb-12">
            {svc.title}{" "}
            <span className="text-white/50">— common questions.</span>
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {svc.faq.map((f, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="border-b border-white/10 py-2"
              >
                <AccordionTrigger
                  className="text-left text-white text-lg font-display hover:no-underline hover:text-[#1F5EFF] py-5"
                  data-testid={`service-faq-trigger-${i}`}
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-white/65 leading-relaxed pb-5 pr-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 md:py-28 overflow-hidden isolate">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 grid-bg opacity-50" />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#1F5EFF]/12 blur-[140px]" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 text-center">
          {soon ? (
            <>
              <h2 className="font-display text-4xl md:text-6xl font-light tracking-tighter text-white leading-tight">
                {svc.title} is{" "}
                <span className="text-white/50">launching soon.</span>
              </h2>
              <p className="mt-6 text-white/60 text-base md:text-lg max-w-xl mx-auto">
                Leave your contacts — we'll notify you the moment bookings open.
              </p>
              <div id="ppf-waitlist" className="mt-10 max-w-xl mx-auto text-left scroll-mt-32">
                <WaitlistForm service={svc.title} testid="service-bottom-waitlist" pkg={selPkg} />
              </div>
              <div className="mt-6">
                <Link
                  to="/services"
                  className="inline-flex items-center gap-3 px-8 py-4 rounded-full border border-white/25 text-white hover:bg-white/5 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" /> Explore Available Services
                </Link>
              </div>
            </>
          ) : (
            <>
              <h2 className="font-display text-4xl md:text-6xl font-light tracking-tighter text-white leading-tight">
                Ready to book your{" "}
                <span className="text-white/50">{svc.title.toLowerCase()}</span>?
              </h2>
              <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
                <button
                  onClick={() => startBooking()}
                  data-testid="service-bottom-cta"
                  className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#1F5EFF] text-white font-semibold hover:bg-white hover:text-black transition-colors blue-glow"
                >
                  Get Exact Price &amp; Book <ArrowRight className="w-4 h-4" />
                </button>
                <Link
                  to="/services"
                  className="inline-flex items-center gap-3 px-8 py-4 rounded-full border border-white/25 text-white hover:bg-white/5 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" /> Explore All Services
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Related services */}
      <section className="relative py-16 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="font-mono-alt text-[10px] text-white/40 mb-6">
            Other Services
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {relatedSlugs.map((s) => {
              const rel = SERVICE_PAGES[s];
              return (
                <Link
                  key={s}
                  to={`/${s}`}
                  className="group bg-[#17171c] border border-white/12 rounded-2xl p-6 hover:border-[#1F5EFF]/40 transition-colors block"
                >
                  <div className="font-mono-alt text-[11px] text-[#1F5EFF]">
                    {rel.tag}
                  </div>
                  <div className="mt-3 font-display text-xl text-white">
                    {rel.title}
                  </div>
                  <div className="mt-4 inline-flex items-center gap-2 text-sm text-white/60 group-hover:text-white transition-colors">
                    Learn more{" "}
                    <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function CatalogPricing({ serviceSlug, onBook }) {
  const [items, setItems] = useState([]);
  const [promo, setPromo] = useState(null);
  useEffect(() => {
    api.get("/pricing/config").then(({ data }) => {
      const keys = PRICING_KEYS[serviceSlug] || [];
      setItems(
        (data.services || [])
          .filter((s) => keys.includes(s.key))
          .sort((a, b) => keys.indexOf(a.key) - keys.indexOf(b.key))
      );
      setPromo(data.promo || null);
    }).catch(() => {});
  }, [serviceSlug]);

  if (!items.length) return null;
  const fmtDur = (m) => (m >= 60 ? `${Math.round(m / 60 * 10) / 10} h` : `${m} min`);
  const isEligible = (s) => promoEligible(promo, s.key);
  // Banner headline reflects THIS page's actual discount: one shared % →
  // "25% OFF" (Ceramic / Paint Correction pages), mixed % → "UP TO 50% OFF".
  const eligiblePcts = [...new Set(items.filter(isEligible).map((s) => promoPctFor(promo, s.key)))];
  const bannerHeadline = eligiblePcts.length === 1
    ? `${eligiblePcts[0]}% OFF`
    : `UP TO ${Math.max(...eligiblePcts)}% OFF`;
  const minPrice = (s) => {
    if (s.options?.length) return Math.min(...s.options.map((o) => o.price));
    if ((s.price_kind || "").startsWith("flat")) return s.flat_price;
    const vals = Object.values(s.prices || {}).filter((v) => v != null);
    return vals.length ? Math.min(...vals) : null;
  };
  const priceText = (s) => {
    if (s.options?.length) return s.options.map((o) => `$${o.price} ${o.key}`).join(" / ");
    if (s.price_kind === "flat") return `$${s.flat_price}${s.unit ? ` / ${s.unit}` : ""}`;
    if (s.price_kind === "flat_starting") return `Starting at $${s.flat_price}`;
    const v = minPrice(s);
    return v != null ? `From $${v}` : "Custom quote";
  };

  return (
    <section className="relative py-20 md:py-28 border-t border-white/10" data-testid="catalog-pricing">
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-8 h-px bg-[#1F5EFF]" />
          <span className="font-mono-alt text-[12px] text-[#1F5EFF]">Packages & Pricing</span>
        </div>
        <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight text-white mb-4">
          Straightforward <span className="text-white/50">studio pricing.</span>
        </h2>
        <p className="text-white/45 text-sm mb-10 max-w-xl">
          The exact price for your vehicle appears in the calculator once you pick its year, make and model.
        </p>
        {items.some(isEligible) && (
          <div data-testid="catalog-promo-banner"
            className="mb-8 rounded-xl border border-amber-400/30 bg-amber-400/10 px-5 py-4 flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="font-display text-2xl md:text-3xl text-amber-300">{bannerHeadline}</span>
            <span className="text-white/75 text-sm">
              {promo.badge} — applied automatically at booking. <span className="text-amber-300/80 font-mono-alt text-[10px] tracking-widest">{promo.limited}</span>
            </span>
          </div>
        )}
        <div className="space-y-2">
          {items.map((s) => {
            const mp = minPrice(s);
            const eligible = isEligible(s) && mp != null;
            return (
            <div key={s.key} data-testid={`catalog-item-${s.key}`}
              className="flex items-center justify-between gap-4 p-5 rounded-xl bg-[#17171c] border border-white/12 hover:border-[#1F5EFF]/40 transition-colors">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white font-medium">{s.name}</span>
                  {s.featured && (
                    <span className="px-2 py-0.5 rounded-full bg-amber-400/15 border border-amber-400/40 text-amber-300 font-mono-alt text-[9px] uppercase tracking-widest">
                      Most Popular
                    </span>
                  )}
                  {eligible && (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-400/15 border border-emerald-400/40 text-emerald-300 font-mono-alt text-[9px] uppercase tracking-widest"
                      data-testid={`catalog-promo-badge-${s.key}`}>
                      {promoPctFor(promo, s.key)}% OFF
                    </span>
                  )}
                </div>
                <div className="text-white/40 text-xs mt-0.5">
                  {fmtDur(s.duration_min)} · {s.group}{s.note ? ` · ${s.note}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-4 flex-shrink-0">
                {eligible ? (
                  <div className="text-right" data-testid={`catalog-promo-price-${s.key}`}>
                    <div className="text-white/35 text-xs line-through tabular-nums">From {fmtMoney(mp)}</div>
                    <div className="font-display text-lg text-emerald-300 tabular-nums">From {fmtMoney(promoPrice(mp, promoPctFor(promo, s.key)))}</div>
                  </div>
                ) : (
                  <div className="font-display text-lg text-[#8db2ff] tabular-nums text-right">{priceText(s)}</div>
                )}
                <button type="button" onClick={() => onBook(s.key)} data-testid={`catalog-book-${s.key}`}
                  className="px-4 py-2 rounded-full border border-white/25 text-white text-xs hover:bg-[#1F5EFF] hover:border-[#1F5EFF] transition-colors">
                  Get Exact Price
                </button>
              </div>
            </div>
            );
          })}
        </div>
        <p className="mt-6 text-white/40 text-xs">Final quote depends on vehicle size and condition — book a free assessment.</p>
      </div>
    </section>
  );
}

function WaitlistForm({ service, testid, pkg }) {  const [f, setF] = useState({ name: "", email: "", phone: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const inp =
    "w-full bg-white/5 border border-white/15 rounded-full px-5 py-3.5 text-white text-sm outline-none focus:border-[#1F5EFF] placeholder:text-white/35";

  const submit = async () => {
    if (!f.name.trim() || !f.email.trim() || !f.phone.trim()) {
      toast.error("Please fill in name, email and phone");
      return;
    }
    setBusy(true);
    try {
      await api.post("/quotes", {
        name: f.name.trim(),
        email: f.email.trim(),
        phone: f.phone.trim(),
        vehicle: "",
        service: `${service} — Launch Waitlist`,
        message: `Notify me when ${service} launches.` + (pkg ? ` Interested in: ${pkg} package.` : ""),
      });
      setDone(true);
      toast.success("You're on the list!");
    } catch (e) {
      toast.error("Something went wrong — please call us instead");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid={`${testid}-done`}
        className="flex items-center gap-3 p-5 rounded-2xl border border-emerald-400/25 bg-emerald-400/10 text-emerald-300 text-sm"
      >
        <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
        You're on the waitlist — we'll reach out the moment {service} bookings open.
      </div>
    );
  }

  return (
    <div data-testid={testid} className="space-y-3">
      {pkg && (
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1F5EFF]/15 border border-[#1F5EFF]/40 text-[#8db2ff] text-[11px] font-mono-alt uppercase tracking-widest" data-testid={`${testid}-pkg`}>
          Selected package: {pkg}
        </div>
      )}
      <div className="grid sm:grid-cols-3 gap-3">
        <input value={f.name} onChange={set("name")} placeholder="Name" className={inp} data-testid={`${testid}-name`} />
        <input value={f.email} onChange={set("email")} type="email" placeholder="Email" className={inp} data-testid={`${testid}-email`} />
        <input value={f.phone} onChange={set("phone")} type="tel" placeholder="Phone" className={inp} data-testid={`${testid}-phone`} />
      </div>
      <button
        onClick={submit}
        disabled={busy}
        data-testid={`${testid}-submit`}
        className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-amber-400 text-black font-semibold hover:bg-white transition-colors disabled:opacity-50"
      >
        <BellRing className="w-4 h-4" />
        {busy ? "Joining…" : "Notify Me at Launch"}
      </button>
    </div>
  );
}
